import { CommonModule } from '@angular/common';
import { Component, inject } from '@angular/core';
import { ActivatedRoute, Router, RouterModule, NavigationEnd } from '@angular/router';
import { Store } from '@ngxs/store';
import { lastValueFrom, Observable, filter, map, catchError } from 'rxjs';
import { StrapiService } from '../../services/strapi.service';

@Component({
  selector: 'app-header',
  imports: [CommonModule, RouterModule],
  standalone: true,
  templateUrl: './header.component.html',
  styleUrl: './header.component.less'
})
export class HeaderComponent {
  public header$!: Observable<any>;
  public languages$!: Observable<any[]>;
  public savedLanguage: any = null;
  public currentLanguage: string = '';
  public urlTranslationCache: { [key: string]: string } = {};
  public isNavbarCollapsed = true;

  private readonly store = inject(Store);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly strapiService = inject(StrapiService);

  ngOnInit(): void {
      // Initialize current language from URL
      this.updateCurrentLanguageFromUrl();

      // Sla huidige taal op om te vergelijken bij routewissel
      let previousLanguage = this.currentLanguage;

      // Listen for navigation events to update current language
      this.router.events.pipe(
        filter(event => event instanceof NavigationEnd)
      ).subscribe(() => {
        const oldLanguage = this.currentLanguage;
        this.updateCurrentLanguageFromUrl();
        // Alleen als de taal verandert, reload header
        if (oldLanguage !== this.currentLanguage) {
          this.loadHeaderForCurrentLanguage();
        }
        this.updateSavedLanguage();
      });

      // Load initial header data
      this.loadHeaderForCurrentLanguage();

      this.languages$ = this.store.select((state) => {
        console.log('Available languages from state:', state);
        return state.globalData?.languages;
      });

      // Stel savedLanguage direct in bij init
      this.updateSavedLanguage();
  }

  private updateSavedLanguage(): void {
    this.languages$?.subscribe(langs => {
      if (langs && langs.length > 0) {
        this.savedLanguage = langs.find(lang => lang.code === this.currentLanguage) || langs[0];
      }
    });
  }

  private loadHeaderForCurrentLanguage(): void {
    // Load header data voor de huidige taal
    this.header$ = this.strapiService.getGlobalData(this.currentLanguage).pipe(
      map(res => {
        // Build URL translation cache from header data
        this.buildUrlTranslationCache(res?.header);
        return res?.header;
      }),
      catchError(error => {
        console.warn('Failed to load header for language, falling back to store data:', error);
        // Fallback naar store data als language-specific call faalt
        return this.store.select((state) => {
          const header = state.globalData?.header;
          this.buildUrlTranslationCache(header);
          return header;
        });
      })
    );
  }

  private buildUrlTranslationCache(header: any): void {
    console.log('Building URL translation cache for language:', this.currentLanguage);
    console.log('Header data:', header);
    
    if (!header?.navbarItems) {
      console.log('No navbar items found in header');
      return;
    }

    // Clear existing cache
    this.urlTranslationCache = {};

    // Hardcoded translations voor veelvoorkomende pagina's
    const staticTranslations: { [key: string]: { [lang: string]: string } } = {
      'praktisch': { 'fr': 'pratique' },
      'contact': { 'fr': 'contact' },
      'home': { 'fr': 'accueil' },
      'about': { 'fr': 'a-propos' },
      'info': { 'fr': 'information' },
      'lopen': { 'fr': 'course' },
      'wandelen': { 'fr': 'marche' },
      'de-nacht': { 'fr': 'la-nuit' },
      'nieuws': { 'fr': 'nouvelles' },
      'sponsors': { 'fr': 'sponsors' }
    };

    // Process navbar items to build cache
    header.navbarItems?.forEach((item: any) => {
      if (item.url) {
        const cleanUrl = item.url.startsWith('/') ? item.url.slice(1) : item.url;
        
        // Als we voor een andere taal zijn dan Nederlands, vertaal de URL
        if (this.currentLanguage !== 'nl' && staticTranslations[cleanUrl] && staticTranslations[cleanUrl][this.currentLanguage]) {
          this.urlTranslationCache[cleanUrl] = staticTranslations[cleanUrl][this.currentLanguage];
          console.log(`Translating ${cleanUrl} to ${staticTranslations[cleanUrl][this.currentLanguage]}`);
        } else {
          // Voor Nederlands of als er geen vertaling is, gebruik originele URL
          this.urlTranslationCache[cleanUrl] = cleanUrl;
        }
      }
      
      // Process sub-menu items
      item.subMenu?.forEach((subItem: any) => {
        if (subItem.url) {
          const cleanUrl = subItem.url.startsWith('/') ? subItem.url.slice(1) : subItem.url;
          
          if (this.currentLanguage !== 'nl' && staticTranslations[cleanUrl] && staticTranslations[cleanUrl][this.currentLanguage]) {
            this.urlTranslationCache[cleanUrl] = staticTranslations[cleanUrl][this.currentLanguage];
            console.log(`Translating submenu ${cleanUrl} to ${staticTranslations[cleanUrl][this.currentLanguage]}`);
          } else {
            this.urlTranslationCache[cleanUrl] = cleanUrl;
          }
        }
        
        // Process sub-sub-menu items
        subItem.subMenu?.forEach((subSubItem: any) => {
          if (subSubItem.url) {
            const cleanUrl = subSubItem.url.startsWith('/') ? subSubItem.url.slice(1) : subSubItem.url;
            
            if (this.currentLanguage !== 'nl' && staticTranslations[cleanUrl] && staticTranslations[cleanUrl][this.currentLanguage]) {
              this.urlTranslationCache[cleanUrl] = staticTranslations[cleanUrl][this.currentLanguage];
              console.log(`Translating subsubmenu ${cleanUrl} to ${staticTranslations[cleanUrl][this.currentLanguage]}`);
            } else {
              this.urlTranslationCache[cleanUrl] = cleanUrl;
            }
          }
        });
      });
    });

    console.log('URL translation cache built:', this.urlTranslationCache);
  }

  private updateCurrentLanguageFromUrl(): void {
    const url = this.router.url;
    console.log('=== UPDATE LANGUAGE FROM URL ===');
    console.log('Current URL:', url);
    
    // Parse language from URL: /nl/page or /fr/page
    const urlParts = url.split('/').filter(part => part.length > 0);
    console.log('URL parts:', urlParts);
    
    const previousLanguage = this.currentLanguage;
    
    if (urlParts.length > 0) {
      this.currentLanguage = urlParts[0];
    } else {
      this.currentLanguage = 'nl'; // fallback
    }
    
    console.log('Previous language:', previousLanguage);
    console.log('Current language set to:', this.currentLanguage);
    
    // Als de taal is veranderd, clear de cache
    if (previousLanguage && previousLanguage !== this.currentLanguage) {
      console.log('Language changed, clearing URL cache');
      this.urlTranslationCache = {};
    }
    
    console.log('=== END UPDATE LANGUAGE ===');
  }


  async setLanguage(targetLang: any) {
    // Parse current page and language from URL
    const url = this.router.url;
    const urlParts = url.split('/').filter(part => part.length > 0);
    
    const currentLanguage = urlParts.length > 0 ? urlParts[0] : 'nl';
    const currentPage = urlParts.length > 1 ? urlParts[1] : 'home';

    // Als we al op de juiste taal zijn, doe niets
    if (currentLanguage === targetLang.code) {
      return;
    }

    try {
      // Voor homepage, gebruik gewoon de basistaal
      if (!currentPage || currentPage === 'home') {
        this.router.navigateByUrl(`/${targetLang.code}/home`);
        return;
      }

      // Voor andere pagina's, zoek de equivalente slug in de doeltaal
      const translatedSlug = await this.findTranslatedSlug(currentPage, currentLanguage!, targetLang.code);
      
      if (translatedSlug) {
        this.router.navigateByUrl(`/${targetLang.code}/${translatedSlug}`);
      } else {
        // Fallback naar homepage als vertaling niet gevonden wordt
        this.router.navigateByUrl(`/${targetLang.code}/home`);
      }
    } catch (error) {
      console.error('Error switching language:', error);
      // Fallback naar homepage bij fouten
      this.router.navigateByUrl(`/${targetLang.code}/home`);
    }
  }

  private async findTranslatedSlug(currentSlug: string, fromLanguage: string, toLanguage: string): Promise<string | null> {
    try {
      console.log(`Finding translation for slug: "${currentSlug}" from "${fromLanguage}" to "${toLanguage}"`);
      
      // Haal eerst de huidige pagina op om het interne ID te krijgen
      const currentPage = await lastValueFrom(this.strapiService.getPageById(currentSlug, fromLanguage));
      
      console.log('Current page result:', currentPage);
      
      if (!currentPage) {
        console.warn('No page found for slug:', currentSlug);
        return null;
      }
      
      if (!currentPage.id || !currentPage.documentId) {
        console.warn('Page found but no ID or documentId:', currentPage);
        return null;
      }

      console.log('Found page with ID:', currentPage.id, 'documentId:', currentPage.documentId);

      // Zoek de vertaalde versie van dezelfde pagina
      const translatedPage = await lastValueFrom(this.strapiService.getPageTranslation(currentPage.documentId, toLanguage));
      
      console.log('Translated page result:', translatedPage);
      
      return translatedPage?.slug || null;
    } catch (error) {
      console.error('Error finding translated slug:', error);
      return null;
    }
  }

  // Helper methode om gelokaliseerde URL's te maken
  async getLocalizedUrl(url: string): Promise<string> {
    // Als het al een absolute URL is (http/https), return as-is
    if (url.startsWith('http')) {
      return url;
    }
    
    // Remove leading slash if present
    const cleanUrl = url.startsWith('/') ? url.slice(1) : url;
    
    // Als we al op de juiste taal zijn, gebruik de originele URL
    if (!this.currentLanguage || this.currentLanguage === 'nl') {
      return `/${this.currentLanguage}/${cleanUrl}`;
    }
    
    // Voor andere talen, probeer de slug te vertalen
    try {
      const translatedSlug = await this.findTranslatedSlug(cleanUrl, 'nl', this.currentLanguage);
      if (translatedSlug) {
        return `/${this.currentLanguage}/${translatedSlug}`;
      }
    } catch (error) {
      console.warn('Could not translate slug:', cleanUrl, error);
    }
    
    // Fallback: gebruik originele URL met huidige taal
    return `/${this.currentLanguage}/${cleanUrl}`;
  }

  // Synchrone variant voor template gebruik
  getLocalizedUrlSync(url: string): string {
    // Als het al een absolute URL is (http/https), return as-is
    if (url.startsWith('http')) {
      return url;
    }

    // Remove leading slash if present
    let cleanUrl = url.startsWith('/') ? url.slice(1) : url;

    // Gebruik translation cache als beschikbaar (zonder taalprefix)
    if (this.urlTranslationCache[cleanUrl]) {
      return `/${this.urlTranslationCache[cleanUrl]}`;
    }

    // Fallback: gebruik originele URL zonder taalprefix
    return `/${cleanUrl}`;
  }

  // Toggle navbar in mobile view
  toggleNavbar(): void {
    this.isNavbarCollapsed = !this.isNavbarCollapsed;
  }

  // Close navbar when clicking on a nav link
  closeNavbar(): void {
    this.isNavbarCollapsed = true;
  }

}
